import { z } from 'zod';
export const VERIFY_METHOD_IDS = [
    'text_code',
    'portal_password',
    'mrn',
    'ssn_last4',
    'security_q',
];
export const VERIFY_METHODS = [
    {
        id: 'text_code',
        name: 'Text a code',
        fields: ['Mobile number', 'Date of birth', 'Code'],
        security: 'Strong',
        tip: 'Code goes only to the number on file',
        notFunctional: true,
    },
    {
        id: 'portal_password',
        name: 'Portal password',
        fields: ['Password', 'First name', 'Last name'],
        security: 'Strong',
        tip: 'Only the client knows it; portal lockout applies',
    },
    {
        id: 'mrn',
        name: 'MRN',
        fields: ['MRN', 'First name', 'Last name'],
        security: 'Medium',
        tip: 'Printed on statements and paperwork',
    },
    {
        id: 'ssn_last4',
        name: 'Last 4 of SSN',
        fields: ['Last 4 SSN', 'First name', 'Last name'],
        security: 'Medium-low',
        tip: 'Only 10,000 possible values; often leaked in breaches',
    },
    {
        id: 'security_q',
        name: 'Security questions',
        fields: ['First name', 'Last name', '2 answers'],
        security: 'Medium-low',
        tip: 'Answers can show up on social media',
    },
];
export const SECURITY_QUESTIONS = [
    { id: 'first_pet', text: 'Name of your first pet' },
    { id: 'first_street', text: 'Street you grew up on' },
    { id: 'childhood_friend', text: 'First name of your childhood best friend' },
    { id: 'first_teacher', text: 'Last name of your first grade teacher' },
    { id: 'first_car', text: 'Make and model of your first car' },
    { id: 'birth_city', text: 'City where your parents met' },
    { id: 'fav_book', text: 'Your favorite childhood book' },
    { id: 'first_job', text: 'Name of your first employer' },
];
export const SECURITY_QUESTION_IDS = SECURITY_QUESTIONS.map((q) => q.id);
export const SECURITY_ANSWERS_REQUIRED = 2;
export const SECURITY_ANSWER_MIN = 3;
export const questionText = (id) => SECURITY_QUESTIONS.find((q) => q.id === id)?.text ?? id;
/** Answers compare case- and spacing-insensitively ("Rex", " rex " and "REX" match). */
export const normalizeAnswer = (s) => String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
/** The mockup's defaults: Portal password on, every question offered, per-form override allowed. */
export function defaultVerificationSettings() {
    return {
        methods: {
            text_code: { enabled: false },
            portal_password: { enabled: true },
            mrn: { enabled: false },
            ssn_last4: { enabled: false },
            security_q: { enabled: false },
        },
        securityQuestions: {
            offered: [...SECURITY_QUESTION_IDS],
            answersRequired: SECURITY_ANSWERS_REQUIRED,
        },
        allowFormOverride: true,
        updatedAt: null,
    };
}
/** Whatever is stored → complete settings (unknown keys dropped, Text a code always off). */
export function mergeVerificationSettings(saved) {
    const base = defaultVerificationSettings();
    if (!saved || typeof saved !== 'object')
        return base;
    const s = saved;
    for (const id of VERIFY_METHOD_IDS) {
        const v = s.methods?.[id]?.enabled;
        if (typeof v === 'boolean')
            base.methods[id].enabled = v;
    }
    base.methods.text_code.enabled = false; // not functional yet
    if (Array.isArray(s.securityQuestions?.offered)) {
        base.securityQuestions.offered = SECURITY_QUESTION_IDS.filter((id) => s.securityQuestions.offered.includes(id));
    }
    if (typeof s.allowFormOverride === 'boolean')
        base.allowFormOverride = s.allowFormOverride;
    if (typeof s.updatedAt === 'string')
        base.updatedAt = s.updatedAt;
    return base;
}
/** 'methods' = none on; 'questions' = security questions on with fewer than 2 offered. */
export function verificationErrors(s) {
    const errors = [];
    if (!VERIFY_METHOD_IDS.some((id) => s.methods[id].enabled))
        errors.push('methods');
    if (s.methods.security_q.enabled &&
        s.securityQuestions.offered.length < s.securityQuestions.answersRequired)
        errors.push('questions');
    return errors;
}
export const verificationSettingsSchema = z
    .object({
    methods: z.object(Object.fromEntries(VERIFY_METHOD_IDS.map((id) => [id, z.object({ enabled: z.boolean() })]))),
    securityQuestions: z.object({ offered: z.array(z.enum(SECURITY_QUESTION_IDS)).max(20) }),
    allowFormOverride: z.boolean(),
})
    .superRefine((v, ctx) => {
    if (v.methods.text_code.enabled)
        ctx.addIssue({ code: 'custom', message: 'Text a code is not available yet.' });
    const merged = mergeVerificationSettings(v);
    const errs = verificationErrors(merged);
    if (errs.includes('methods'))
        ctx.addIssue({ code: 'custom', message: 'Turn on at least one method.' });
    if (errs.includes('questions'))
        ctx.addIssue({ code: 'custom', message: 'Offer at least 2 questions.' });
});
/** #311: set a client's 2 answers. Distinct questions, at least 3 characters, different answers. */
export const securityAnswersSchema = z
    .object({
    answers: z
        .array(z.object({
        questionId: z.enum(SECURITY_QUESTION_IDS),
        answer: z.string().max(200),
    }))
        .length(SECURITY_ANSWERS_REQUIRED),
})
    .superRefine((v, ctx) => {
    const [a, b] = v.answers;
    if (!a || !b)
        return;
    if (a.questionId === b.questionId)
        ctx.addIssue({ code: 'custom', message: 'Choose two different questions.' });
    for (const x of v.answers)
        if (normalizeAnswer(x.answer).length < SECURITY_ANSWER_MIN)
            ctx.addIssue({
                code: 'custom',
                message: `Use at least ${SECURITY_ANSWER_MIN} characters for each answer.`,
            });
    if (normalizeAnswer(a.answer) === normalizeAnswer(b.answer))
        ctx.addIssue({ code: 'custom', message: 'Use a different answer for each question.' });
});
/**
 * ehr#310 - the Form Builder's QR check-in (Jay 10-09): a client in the waiting room scans the
 * form's QR code and says who they are with first name, last name, date of birth and ZIP. The
 * Form Builder asks the EHR (server to server, service token) to match them to one existing
 * client; the form opens filled in from that client's Demographics, and the answers land in their
 * chart under Forms. The client must already be in the EHR.
 */
export const qrVerifyClientSchema = z.object({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    /** As typed: YYYY-MM-DD or MM/DD/YYYY. */
    dob: z.string().trim().min(6).max(20),
    zip: z.string().trim().min(5).max(10),
});
/** `submittedBy` on a form_submissions row a client sent through a QR code (no staff user). */
export const QR_FORM_SUBMITTER = 'client:qr';
/** A QR check-in form's answers, sent by the Form Builder once the client submits. */
export const qrFormSubmissionSchema = z.object({
    /** The Form Builder form: its id (the EHR form's externalId), title and layout. */
    form: z.object({
        id: z.string().trim().min(1).max(100),
        title: z.string().trim().max(300).default(''),
        description: z.string().max(5000).nullish(),
        schema: z.record(z.unknown()).nullish(),
    }),
    /** Field id (or `<id>::<row>` for a rating-scale row) → the answer as text. */
    answers: z
        .record(z.string().max(100_000))
        .refine((a) => Object.keys(a).length <= 2000, 'Too many answers'),
});
/**
 * alertshub#4: a form answered through a Form Builder public link (no staff sign-in on a public
 * fill), sent by the Form Builder so the EHR can post the staff card. Titles and ids only, never
 * answers.
 */
export const fbsLinkSubmittedSchema = z.object({
    /** The Form Builder submission (the card's key) and its form (the Inbox opens on both). */
    submissionId: z.string().trim().min(1).max(100),
    formId: z.string().trim().min(1).max(100),
    formTitle: z.string().trim().max(300).default(''),
    /** A consent form: "Client signed a consent" instead of "Client submitted a form". */
    consent: z.boolean().default(false),
    /** The EHR client, when the link knows them (a QR check-in matched in the EHR). */
    contactId: z.string().trim().min(1).max(100).nullish(),
    /** Who made the link (or owns the form), matched to an EHR user by email. */
    senderEmail: z.string().trim().max(254).nullish(),
    /**
     * alertshub#5: the form's scoring sections that crossed a risk rule, scored by the Form Builder
     * (an alert fired, or the total is in the top, high-severity band). Empty: no risk card.
     */
    risk: z
        .array(z.object({
        name: z.string().trim().max(120).default(''),
        score: z.number().finite(),
        band: z.string().trim().max(120).nullish(),
        message: z.string().trim().max(500).default(''),
    }))
        .max(20)
        .default([]),
});
//# sourceMappingURL=verification.js.map